// The frame-time governor: a phone that can't hold the frame gives back the
// least-missed detail first, one rung at a time, and takes it back once the
// frame has room again.
//
// Everything else in the client is chosen once, at load: the graphics tier,
// the DPR cap, lite water and effects. On a phone that is a guess made before
// the first sortie, and the air map guessed wrong: 27 fps with the main thread
// pinned, the cull walk and the draws half the frame. The governor watches the
// frame the player actually gets and walks a short ladder:
//
//   rung 1  thinner effects   half the block debris a burst throws
//   rung 2  the near ring     chunks (and their trees) to ~1.7 km, not 2.4
//   rung 3  the far models    every airframe, gun and hull takes its coarser
//                             level at LOD_BIAS of the usual distance
//   rung 4  the pixel ratio   1.5 → 1.0, the fill a phone GPU pays for
//
// in that order down, and in reverse up. It reads the WALL clock (helm.js
// frameClocks' wallDt), never the frame's clamped dt: under 10 fps the
// clamped clock reads 0.1 s whatever the frame cost, and a governor watching
// it would never see how bad things got.
//
// It judges a window, not a frame. A two-second window's MEDIAN interval is
// the frame the player is living with; one 80 ms hitch from a chunk landing or
// a GC is not, and the median shrugs it off. Down is quick (one bad window),
// up is slow (several good ones in a row) and the thresholds stand apart, so a
// rung that bought the frame back isn't handed back the moment it works. A
// rung that is handed back and then has to be taken again doubles the wait
// before the next try: a phone that sits right on the line settles on the
// lower rung instead of trading the far hills back and forth all round.
//
// A desktop at 60 fps never trips it (its median sits at 16.7 ms against a
// 25 ms line), and by default only touch devices run it at all; ?gov=on|off
// pins it either way (gfx.js GOVERNOR_FORCED). Nothing is said to the player:
// the only thing they should notice is a steadier frame.

/** The rungs, top to bottom. A level is how many of them are engaged. */
export const GOV_RUNGS = ["fx", "ring", "lod", "dpr"];

/** Rung 3's reach: models reckon the camera this much farther than it is. */
export const GOV_LOD_BIAS = 1.6;

/** Below 40 fps over a window, step down a rung. */
export const GOV_DOWN_S = 1 / 40;
/** Above 52 fps over a window, that window counts toward stepping back up. A
 *  60 Hz phone running clean reads 16.7 ms; the gap to the 25 ms line is the
 *  hysteresis that keeps a rung that worked from being handed straight back. */
export const GOV_UP_S = 1 / 52;
/** Seconds of frames one judgement weighs. */
export const GOV_WINDOW_S = 2;
/** Good windows in a row before a rung comes back (so 8 s of clean frames). */
export const GOV_UP_WINDOWS = 4;
/** A rung taken again inside this many seconds of being handed back doubles
 *  the up wait, to at most GOV_UP_WINDOWS_MAX windows (five minutes: a phone
 *  on the line tries the far hills again a couple of times a round, not every
 *  minute). A hand-back that holds this long resets the wait. */
export const GOV_RELAPSE_S = 12;
export const GOV_UP_WINDOWS_MAX = 150;
/** A frame this long is not a frame: a backgrounded tab, a loading stall, a
 *  debugger. It voids the window rather than voting in it. */
export const GOV_STALL_S = 1;

const WINDOW_CAP = 1024; // a 2 s window at 500 fps; any more and we stop counting

export class FrameGovernor {
  /**
   * @param {object} o
   * @param {number} [o.maxLevel] how many rungs this machine has (a DPR-1
   *   screen has no pixel ratio to give back: GOV_RUNGS.length - 1)
   * @param {(level: number, prev: number) => void} [o.apply] engage the rungs
   *   for a new level; called only on a change
   */
  constructor({ maxLevel = GOV_RUNGS.length, apply = () => {}, pinned = null } = {}) {
    this.maxLevel = maxLevel;
    this.apply = apply;
    this.level = 0;
    // A probe's ?gov=N: that many rungs, held whatever the frame does.
    this.pinned = pinned;
    this.samples = new Float32Array(WINDOW_CAP);
    this.n = 0;
    this.sum = 0;
    this.clock = 0; // wall seconds the governor has watched
    this.good = 0; // good windows in a row
    this.upWindows = GOV_UP_WINDOWS;
    this.raisedAt = -Infinity; // when a rung was last handed back
    this.probation = false; // that hand-back hasn't yet held GOV_RELAPSE_S
    this.lastMedian = 0; // the last window's verdict, for the probes
    this.steps = 0; // level changes, for the probes
  }

  /** One rendered frame, `wallDt` seconds of it. Returns the level. */
  sample(wallDt) {
    if (this.pinned !== null) {
      if (this.level !== this.pinned) this.set(Math.min(this.pinned, this.maxLevel));
      return this.level;
    }
    if (!(wallDt > 0)) return this.level;
    if (wallDt >= GOV_STALL_S) {
      // Not a frame the player sat through at this rate; start the window over.
      this.n = 0;
      this.sum = 0;
      return this.level;
    }
    this.clock += wallDt;
    if (this.n < WINDOW_CAP) this.samples[this.n++] = wallDt;
    this.sum += wallDt;
    if (this.sum >= GOV_WINDOW_S) this.judge();
    return this.level;
  }

  judge() {
    const m = median(this.samples, this.n);
    this.n = 0;
    this.sum = 0;
    this.lastMedian = m;
    const held = this.clock - this.raisedAt >= GOV_RELAPSE_S;
    if (m > GOV_DOWN_S) {
      this.good = 0;
      if (this.level < this.maxLevel) {
        // Taken straight back after being handed back: wait longer next time.
        if (this.probation && !held) {
          this.upWindows = Math.min(this.upWindows * 2, GOV_UP_WINDOWS_MAX);
        }
        this.probation = false;
        this.set(this.level + 1);
      }
      return;
    }
    if (this.probation && held) {
      // The hand-back stuck: the machine (or the fight) changed. Back to the
      // usual wait for the next rung.
      this.probation = false;
      this.upWindows = GOV_UP_WINDOWS;
    }
    if (m < GOV_UP_S) {
      if (this.level > 0 && ++this.good >= this.upWindows) {
        this.good = 0;
        this.raisedAt = this.clock;
        this.probation = true;
        this.set(this.level - 1);
      }
    } else {
      // Between the lines: the frame is fine where it is. Hold, and don't let
      // a good window either side of a middling one count as a streak.
      this.good = 0;
    }
  }

  set(level) {
    const prev = this.level;
    this.level = level;
    this.steps++;
    this.apply(level, prev);
  }

  /** Whether `rung` (a GOV_RUNGS name) is engaged at the current level. */
  engaged(rung) {
    return GOV_RUNGS.indexOf(rung) < this.level;
  }

  /** The readout the probes and the e2e read off window.__va.governor. */
  state() {
    return {
      level: this.level,
      maxLevel: this.maxLevel,
      rungs: GOV_RUNGS.slice(0, this.level),
      medianMs: Math.round(this.lastMedian * 1e4) / 10,
      steps: this.steps,
      upWindows: this.upWindows,
      pinned: this.pinned,
    };
  }
}

/** The median of the first n values (sorted in place: the window is spent). */
function median(a, n) {
  if (!n) return 0;
  const s = a.subarray(0, n).sort();
  return n & 1 ? s[n >> 1] : (s[n / 2 - 1] + s[n / 2]) / 2;
}
